import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UseTourGuideReturn } from "./useTourGuide";
import {
  getLoggedInUser,
  getTourGuidesLoading,
} from "@store/selectors/entities/auth";
import { fetchTourGuides } from "@store/thunks/userProfile";
import type { TourGuideType } from "@root/constants/tour-guide";
import {
  getTourCompleted,
  setTourCompleted,
  isUnauthTourType,
} from "@root/utils/tour-guide-storage";

export interface UseAutoTourGuideProps {
  tourGuide: UseTourGuideReturn;
  tourGuideType: TourGuideType;
  isOnTourPath: boolean;
  onTourComplete?: () => void;
}

export interface UseAutoTourGuideReturn extends UseTourGuideReturn {
  // Extended functionality
}
export const useAutoTourGuide = ({
  tourGuide,
  tourGuideType,
  isOnTourPath,
  onTourComplete,
}: UseAutoTourGuideProps): UseAutoTourGuideReturn => {
  const dispatch = useDispatch();
  const user = useSelector(getLoggedInUser) as any;
  const tourGuidesLoading = useSelector(getTourGuidesLoading);
  const hasAutoStartedRef = useRef(false);
  const fetchDoneRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const hadLoadingRef = useRef(false);

  const isUnauth = isUnauthTourType(tourGuideType);

  // ✅ FIX: Check if tour guides data is actually loaded
  const hasTourGuidesData = user?.tourGuides !== undefined;

  // Check if user is new (just signed up) - tourGuides is undefined or empty array after fetch
  // If tourGuides has any entries (even if not completed), user is existing
  const isNewUser =
    user &&
    hasTourGuidesData &&
    (!user?.tourGuides || user?.tourGuides?.length === 0);

  console.log("new user", isNewUser);

  const isTourCompleted = user
    ? Boolean(
        user?.tourGuides?.some(
          (t: { type: number; isDone: boolean }) =>
            t.type === tourGuideType && t.isDone === true,
        ),
      )
    : isUnauth
      ? getTourCompleted(tourGuideType)
      : false;

  // Fetch logic
  useEffect(() => {
    if (!isOnTourPath || !user) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    dispatch(fetchTourGuides() as any);
  }, [isOnTourPath, user, dispatch]);

  // Track loading states
  useEffect(() => {
    if (tourGuidesLoading) {
      hadLoadingRef.current = true;
    } else if (hadLoadingRef.current && isOnTourPath && user) {
      fetchDoneRef.current = true;
    }
  }, [tourGuidesLoading, isOnTourPath, user]);

  // Auto-start logic
  useEffect(() => {
    const runAutoStartCheck = () => {
      // Tour guide disabled for first-time login users — set to original condition to re-enable
      const authenticatedAutoStart = false;
      // isOnTourPath &&
      // user &&
      // hasTourGuidesData &&
      // !tourGuidesLoading &&
      // fetchDoneRef.current &&
      // isNewUser &&
      // !isTourCompleted &&
      // !tourGuide.isActive &&
      // !hasAutoStartedRef.current;

      // Tour guide disabled for unauthenticated users — set to false to re-enable
      const unauthenticatedAutoStart = false;
      // isOnTourPath &&
      // !user &&
      // isUnauth &&
      // !isTourCompleted &&
      // !tourGuide.isActive &&
      // !hasAutoStartedRef.current;

      const shouldAutoStart =
        authenticatedAutoStart || unauthenticatedAutoStart;

      if (shouldAutoStart) {
        hasAutoStartedRef.current = true;
        tourGuide.startTour();
      }
    };

    const id = setTimeout(runAutoStartCheck, 250);
    return () => clearTimeout(id);
  }, [
    isOnTourPath,
    user,
    hasTourGuidesData, // ✅ NEW: Add to dependencies
    tourGuidesLoading,
    isNewUser, // ✅ FIX: Add to dependencies
    isTourCompleted,
    isUnauth,
    tourGuideType,
    tourGuide.isActive,
    tourGuide.startTour,
  ]);

  const stopTour = useCallback(() => {
    tourGuide.stopTour();
  }, [tourGuide]);

  const nextStep = useCallback(() => {
    const isLastStep = tourGuide.currentStep === tourGuide.totalSteps - 1;

    if (isLastStep) {
      if (user) {
        onTourComplete?.();
      } else if (isUnauth) {
        setTourCompleted(tourGuideType);
      }
    }

    tourGuide.nextStep();
  }, [tourGuide, onTourComplete, user, isUnauth, tourGuideType]);

  return {
    ...tourGuide,
    stopTour,
    nextStep,
  };
};
